import React, { useEffect, useState } from 'react';
import Auth from './components/Auth';
import Timer from './components/Timer';
import History from './components/History';
import LogModal from './components/LogModal';
import { LogOutIcon } from './components/ui/Icons';
import { authService, dataService, isUsingFirebase } from './services/firebase';
import { UserProfile, LogEntry, TimerMode, TimerSettings } from './types';

const App: React.FC = () => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Timer State
  // Default focus set to 30 to match one of the selectable options
  const [mode, setMode] = useState<TimerMode>('focus');
  const [settings, setSettings] = useState<TimerSettings>({ focusDuration: 30, restDuration: 5 });
  
  // Logging State
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [pendingLog, setPendingLog] = useState<{duration: number, startTime: string, endTime: string} | null>(null);
  const [showLogModal, setShowLogModal] = useState(false);
  
  // Editing State
  const [editingLog, setEditingLog] = useState<LogEntry | null>(null);

  useEffect(() => {
    const unsubscribe = authService.onAuthStateChanged((u) => {
      setUser(u);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (user) {
      const unsubscribe = dataService.subscribeToLogs(user.uid, (fetchedLogs) => {
        setLogs(fetchedLogs);
      });
      return unsubscribe;
    }
  }, [user]);

  const handleTimerComplete = (duration: number, startTime: string, endTime: string) => {
    setPendingLog({ duration, startTime, endTime });
    setShowLogModal(true);
  };

  const handleTimerStart = async () => {
    if (!user || logs.length === 0) return;

    // Logic: If there is a gap between now and the last log's end time, 
    // record it as 'Unplanned'
    
    // Sort logs just in case (though firebase usually returns sorted) to get the latest
    const latestLog = [...logs].sort((a, b) => b.timestamp - a.timestamp)[0];
    
    if (!latestLog) return;

    const lastEndTime = new Date(latestLog.endTime).getTime();
    const now = Date.now();
    const diffMs = now - lastEndTime;
    const diffSeconds = Math.floor(diffMs / 1000);
    
    // If gap is greater than 1 minute, log it
    // Using 60 seconds threshold to avoid tiny gaps due to UI interaction time
    if (diffSeconds > 60) {
        // Ensure the gap belongs to "today" (simple check: if gap > 12 hours, assume overnight and ignore)
        // or just log it. Let's log it but maybe users will delete it if it's overnight. 
        // Better: Only log if < 12 hours.
        if (diffMs < 12 * 60 * 60 * 1000) {
            await dataService.addLog({
                userId: user.uid,
                date: new Date(lastEndTime).toISOString().split('T')[0], // associate with the day it started? or today? Let's use today.
                startTime: latestLog.endTime,
                endTime: new Date().toISOString(),
                duration: diffSeconds,
                type: 'unplanned',
                notes: 'Time between sessions',
                category: 'Unplanned',
                timestamp: Date.now() - 1 // slight offset to appear before the new session
            });
        }
    }
  };

  const handleLogSubmit = async (notes: string, category: string) => {
    if (!user || !pendingLog) return;

    // Save Log
    await dataService.addLog({
        userId: user.uid,
        date: pendingLog.startTime.split('T')[0],
        startTime: pendingLog.startTime,
        endTime: pendingLog.endTime,
        duration: pendingLog.duration,
        type: mode,
        notes,
        category,
        timestamp: Date.now()
    });

    setShowLogModal(false);
    setPendingLog(null);
  };

  const handleDiscardLog = () => {
    setShowLogModal(false);
    setPendingLog(null);
    setEditingLog(null);
  };

  const handleEditLog = (entry: LogEntry) => {
    setEditingLog(entry);
    setShowLogModal(true);
  };
  
  const handleUpdateLog = async (notes: string, category: string) => {
    if(!editingLog) return;
    await dataService.updateLog(editingLog.id, { notes, category });
    setEditingLog(null);
    setShowLogModal(false);
  }

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading Mindul Flow...</div>;
  if (!user) return <Auth />;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col md:flex-row">
      
      {/* Sidebar / Topbar for Mobile */}
      <div className="w-full md:w-20 bg-white border-b md:border-b-0 md:border-r border-slate-200 flex md:flex-col justify-between items-center p-4 md:py-8 z-10 sticky top-0">
         <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center text-white font-bold text-xl">
            M
         </div>
         <button 
            onClick={() => authService.logout()} 
            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            title="Sign Out"
         >
            <LogOutIcon />
         </button>
      </div>

      <main className="flex-1 p-4 md:p-8 overflow-y-auto max-h-screen">
        <div className="max-w-6xl mx-auto grid grid-cols-1 xl:grid-cols-3 gap-8">
            
            {/* Left Column: Timer */}
            <div className="xl:col-span-1">
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 sticky top-8">
                    {!isUsingFirebase && (
                        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                            Data is saved locally to this browser. Export them if you want to use other browsers
                        </div>
                    )}
                    <div className="mb-6 text-center">
                         <h2 className="text-lg font-semibold text-slate-800">
                             Good {new Date().getHours() < 12 ? 'Morning' : 'Afternoon'}, 
                         </h2>
                         <p className="text-sm text-slate-500 truncate max-w-[200px] mx-auto">{user.email}</p>
                    </div>
                    
                    <Timer 
                        mode={mode}
                        settings={settings}
                        onSettingsChange={setSettings}
                        onTimerComplete={handleTimerComplete}
                        onTimerStart={handleTimerStart}
                        onModeChange={setMode}
                    />
                </div>
            </div>

            {/* Right Column: History & Stats */}
            <div className="xl:col-span-2 space-y-8">
                <History logs={logs} onEdit={handleEditLog} />
            </div>

        </div>
      </main>

      {/* Logic Modal handles both New Entry and Editing */}
      <LogModal 
        isOpen={showLogModal}
        onClose={handleDiscardLog}
        onDiscard={!editingLog ? handleDiscardLog : undefined}
        onSubmit={editingLog ? handleUpdateLog : handleLogSubmit}
        mode={editingLog ? editingLog.type : mode}
        durationMinutes={editingLog ? editingLog.duration / 60 : (pendingLog ? pendingLog.duration / 60 : 0)}
        defaultNotes={editingLog ? editingLog.notes : ''}
        defaultCategory={editingLog ? editingLog.category : ''}
      />
    </div>
  );
};

export default App;